import { ChatTextIcon, ClockIcon, PhoneIcon } from '@phosphor-icons/react'
import type { ReactElement } from 'react'
import { getPreferredSnoozeDays } from 'app/components/action-center/momentumStore'
import { RecallTimeline } from 'app/components/action-center/RecallTimeline'
import type { ContactCardFacts } from 'app/components/lists/card/contactCardFacts'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { cn } from 'app/lib/utils'
import type { ContactJson } from 'app/types/entities'

/**
 * The recall timeline or the phone number, then the hover actions. A lead's actions sit
 * pinned to the bottom right corner instead, over the communications line.
 */
export function BottomSection({
  contact,
  derived,
  variant,
  onText,
  onSnooze,
}: {
  contact: ContactJson
  derived: ContactCardFacts
  variant: `hygiene` | `lead`
  onText?: () => void
  onSnooze?: () => void
}): ReactElement {
  const { recall } = derived
  const lead = variant === `lead`
  const snoozeDays = getPreferredSnoozeDays()
  return (
    <div
      className={cn(
        lead
          ? `absolute bottom-2.5 right-3 flex items-center gap-1 bg-surface pl-2`
          : `mt-2 flex items-center justify-between gap-2`,
      )}
    >
      {!lead &&
        (recall ? (
          <div className="min-w-0 flex-1">
            <RecallTimeline contact={contact} />
          </div>
        ) : contact.cellPhone ? (
          // draggable={false}: a plain <a> is natively draggable, and without
          // it the card's own div never gets a `dragstart` when the pointer
          // starts on the phone number.
          <a
            href={`tel:${contact.cellPhone}`}
            draggable={false}
            onClick={(event) => event.stopPropagation()}
            className="truncate text-body-xs tabular-nums text-subtle transition hover:text-strong"
          >
            {formatPhoneNumber(contact.cellPhone)}
          </a>
        ) : (
          <span className="truncate text-body-xs text-minimal">No mobile number</span>
        ))}
      <span className="flex shrink-0 items-center gap-1 opacity-0 transition group-focus-within:opacity-100 group-hover:opacity-100">
        {contact.cellPhone && (
          <>
            <a
              href={`tel:${contact.cellPhone}`}
              draggable={false}
              onClick={(event) => event.stopPropagation()}
              aria-label="Call this contact"
              className="rounded-md border border-default bg-surface p-1 text-subtle transition hover:border-strong hover:bg-surface-subtle hover:text-strong"
            >
              <PhoneIcon size={13} weight="bold" />
            </a>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                onText?.()
              }}
              aria-label="Text this contact"
              className="rounded-md border border-default bg-surface p-1 text-subtle transition hover:border-strong hover:bg-surface-subtle hover:text-strong"
            >
              <ChatTextIcon size={13} weight="bold" />
            </button>
          </>
        )}
        {/* Outside the phone gate: snoozing is the one action that does not
            depend on having a number. */}
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onSnooze?.()
          }}
          aria-label={`Snooze for ${snoozeDays} days`}
          title={`Snooze for ${snoozeDays} days`}
          className="rounded-md border border-default bg-surface p-1 text-subtle transition hover:border-strong hover:bg-surface-subtle hover:text-strong"
        >
          <ClockIcon size={13} weight="bold" />
        </button>
      </span>
    </div>
  )
}
